/**
 * services/adaptive/adaptiveScoring.ts
 *
 * Adaptive Scoring — MER Algo Footprints v7.1B
 *
 * Synthesis layer that combines DQS, adaptive confidence, adaptive setup
 * weighting, and exposure intelligence into a single compact overlay score.
 *
 * OBSERVATIONAL ONLY. Not a recommendation. Not execution logic.
 * Does not feed engine logic, mutate stored data, or alter risk behavior.
 *
 * Non-executing. Decision support only.
 */

import type { AuditRecord }               from '../../../../types';
import type { DecisionQualityResult }     from '../quant/decisionQuality';
import type { AdaptiveConfidenceSummary } from './adaptiveConfidence';
import type { AdaptiveSetupWeightsSummary } from './adaptiveSetupWeights';
import type { ExposureIntelligenceSummary } from './exposureIntelligence';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AdaptiveScoreAdjustments {
  confidence: number;   // −10 to +5  (from adaptive confidence bucket)
  context:    number;   // −8  to +8  (from adaptive setup weight)
  exposure:   number;   // −10 to  0  (from exposure warnings)
}

export interface AdaptiveScoreResult {
  baseDqs:     number;
  score:       number;   // 0–100 final adaptive score
  adjustments: AdaptiveScoreAdjustments;
  summary:     string;
}

// ─── Component adjustments ────────────────────────────────────────────────────

function confidenceAdjustment(
  record: AuditRecord,
  adaptive: AdaptiveConfidenceSummary,
): number {
  const match = adaptive.buckets.find(b => b.bucket === record.confidenceBucket);
  if (!match || match.adjustedBias === 'insufficient_data') return 0;
  // Bound: min −10, max +5
  return Math.max(-10, Math.min(5, match.confidenceAdjustment));
}

function contextAdjustment(
  record: AuditRecord,
  weights: AdaptiveSetupWeightsSummary,
): number {
  const match = weights.contexts.find(
    c => c.setupType === record.setupType &&
         c.regime    === record.regime    &&
         c.phase     === record.phase,
  );
  if (!match) return 0;
  const w = match.adjustedWeight;
  if (w >= 1.20) return +8;
  if (w >= 1.10) return +5;
  if (w >= 1.02) return +2;
  if (w <= 0.80) return -8;
  if (w <= 0.90) return -5;
  if (w <  1.00) return -2;
  return 0;
}

function exposureAdjustment(exposure: ExposureIntelligenceSummary): number {
  if (!exposure.warnings.length) return 0;
  let penalty = 0;
  for (const w of exposure.warnings) {
    if (w.severity === 'high')   penalty -= 8;
    else if (w.severity === 'medium') penalty -= 4;
    else if (w.severity === 'low')    penalty -= 1;
  }
  // Cap: max −10, do not stack aggressively
  return Math.max(-10, penalty);
}

// ─── Main exports ─────────────────────────────────────────────────────────────

export function computeAdaptiveScore(
  record:         AuditRecord,
  dqs:            DecisionQualityResult,
  adaptive:       AdaptiveConfidenceSummary,
  weights:        AdaptiveSetupWeightsSummary,
  exposure:       ExposureIntelligenceSummary,
): AdaptiveScoreResult {
  const baseDqs  = dqs.score;
  const confAdj  = confidenceAdjustment(record, adaptive);
  const ctxAdj   = contextAdjustment(record, weights);
  const expAdj   = exposureAdjustment(exposure);

  const score = Math.max(0, Math.min(100, baseDqs + confAdj + ctxAdj + expAdj));

  const parts: string[] = [];
  if (confAdj !== 0) parts.push(`confidence ${confAdj > 0 ? '+' : ''}${confAdj}`);
  if (ctxAdj  !== 0) parts.push(`context ${ctxAdj > 0 ? '+' : ''}${ctxAdj}`);
  if (expAdj  !== 0) parts.push(`exposure ${expAdj}`);
  const adjStr = parts.length ? `; ${parts.join(', ')}` : '';

  const summary = `Adaptive Score ${score}/100 (base DQS ${baseDqs}${adjStr}).`;

  return {
    baseDqs,
    score,
    adjustments: { confidence: confAdj, context: ctxAdj, exposure: expAdj },
    summary,
  };
}

/** Lightweight aggregate: avg adaptive score + top/bottom 5 by adaptive score. */
export interface AdaptiveScoreSummary {
  avgScore:       number | null;
  topDecisions:   Array<{ id: string; symbol: string; setupType: string; baseDqs: number; score: number }>;
  bottomDecisions: Array<{ id: string; symbol: string; setupType: string; baseDqs: number; score: number }>;
}

export function computeAdaptiveScoreSummary(
  records:  AuditRecord[],
  dqsMap:   Map<string, DecisionQualityResult>,
  adaptive: AdaptiveConfidenceSummary,
  weights:  AdaptiveSetupWeightsSummary,
  exposure: ExposureIntelligenceSummary,
): AdaptiveScoreSummary {
  if (!records.length) {
    return { avgScore: null, topDecisions: [], bottomDecisions: [] };
  }

  const scored = records
    .map(r => {
      const dqs = dqsMap.get(r.id);
      if (!dqs) return null;
      const result = computeAdaptiveScore(r, dqs, adaptive, weights, exposure);
      return { r, result };
    })
    .filter((x): x is { r: AuditRecord; result: AdaptiveScoreResult } => x !== null);

  if (!scored.length) return { avgScore: null, topDecisions: [], bottomDecisions: [] };

  const avgScore = Math.round(
    scored.reduce((s, { result }) => s + result.score, 0) / scored.length,
  );

  const toEntry = ({ r, result }: { r: AuditRecord; result: AdaptiveScoreResult }) => ({
    id:        r.id,
    symbol:    r.symbol,
    setupType: r.setupType,
    baseDqs:   result.baseDqs,
    score:     result.score,
  });

  const sortedDesc = [...scored].sort((a, b) => b.result.score - a.result.score);
  const sortedAsc  = [...scored].sort((a, b) => a.result.score - b.result.score);

  return {
    avgScore,
    topDecisions:    sortedDesc.slice(0, 5).map(toEntry),
    bottomDecisions: sortedAsc.slice(0, 5).map(toEntry),
  };
}
